import React, { FC, useCallback, useState } from "react";

import { defineMessages, useIntl } from "react-intl";

import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";
import { addSchemaIfEmpty, isValidUrl, removeSchemaFromUrl } from "@mapmycustomers/shared/util/url";
import { TextField } from "@mapmycustomers/ui";

import { IWizardStep, StandardStep } from "@app/component/wizard";
import useStateWithTransformer from "@app/util/hook/useStateWithTransformer";

import { AddResourcesWizardState } from ".";
import styles from "./AddResourcesWizard.module.scss";

const messages = defineMessages({
  addLink: {
    id: "resources.add.wizard.addLink",
    defaultMessage: "Add link",
    description: "Add link header title",
  },
  cancel: {
    id: "resources.add.wizard.addLink.cancel",
    defaultMessage: "Cancel",
    description: "Cancel button",
  },
  invalidUrl: {
    id: "resources.add.wizard.addLink.invalidUrl",
    defaultMessage: "URL is invalid",
    description: "URL field invalid on add link step",
  },
  name: {
    id: "resources.add.wizard.addLink.name",
    defaultMessage: "Name",
    description: "Name field label",
  },
  namePlaceholder: {
    id: "resources.add.wizard.addLink.name.placeholder",
    defaultMessage: "Type name...",
    description: "Name field placeholder",
  },
  url: {
    id: "resources.add.wizard.addLink.url",
    defaultMessage: "URL",
    description: "URL field label",
  },
  urlPlaceholder: {
    id: "resources.add.wizard.addLink.url.placeholder",
    defaultMessage: "Type url...",
    description: "URL field placeholder",
  },
});

export interface AddLinkStepProps extends IWizardStep<AddResourcesWizardState> {}

const AddLinkStep: FC<AddLinkStepProps> = ({ onCancel, onGoForward, onSetState, ...props }) => {
  const intl = useIntl();
  const [name, setName] = useState<string>("");
  const [url, setUrl] = useStateWithTransformer<string>("", removeSchemaFromUrl);
  const [urlError, setUrlError] = useState<string | undefined>();

  const handleNext = useCallback(() => {
    if (isValidUrl(url)) {
      setUrlError(undefined);
      onSetState({ linkName: name, url: addSchemaIfEmpty(url) });
      onGoForward();
    } else {
      setUrlError(intl.formatMessage(messages.invalidUrl));
    }
  }, [intl, name, onGoForward, onSetState, url]);

  return (
    <StandardStep
      {...props}
      backButtonText={intl.formatMessage(messages.cancel)}
      nextButtonDisabled={isEmptyString(name) || isEmptyString(url)}
      onCancel={onCancel}
      onGoBack={onCancel}
      onGoForward={handleNext}
      title={intl.formatMessage(messages.addLink)}
    >
      <div className={styles.addLinkContainer}>
        <TextField
          label={intl.formatMessage(messages.name)}
          onChange={setName}
          placeholder={intl.formatMessage(messages.namePlaceholder)}
          required
          value={name}
        />
        <TextField
          addonBefore="https://"
          error={urlError}
          label={intl.formatMessage(messages.url)}
          onChange={setUrl}
          placeholder={intl.formatMessage(messages.urlPlaceholder)}
          required
          value={url}
        />
      </div>
    </StandardStep>
  );
};

export default AddLinkStep;
